import * as React from 'react';
import { Modal, Input, FormPanel, SubmitButton } from '@watcherr/ui';
import { t } from '~/i18n';
import { HcpGroup } from '@watcherr/resources';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useStores } from '~store/utils';
import { UserAction } from '~config/notification';

interface HcpGroupCreateEditModalProps {
  onClose: () => void;
  groupId?: string;
  groupName?: string;
}

export const HcpGroupCreateEditModal: React.FC<HcpGroupCreateEditModalProps> = ({
  onClose,
  groupId,
  groupName: initialGroupName,
}) => {
  const [groupName, setGroupName] = React.useState(initialGroupName || '');
  const queryClient = useQueryClient();
  const { notifications } = useStores();

  const isEditMode = !!groupId;

  const createMutation = useMutation({
    mutationFn: (name: string) => HcpGroup.createHcpGroup({ hcpGroupName: name }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['hcpGroups'] });
      notifications.addUserActionNotification({
        notificationType: 'success',
        message: t('hcpGroups.create.success', { name: data.hcpGroupName }),
      });
      onClose();
    },
    onError: (error) => {
      console.error('Error creating HCP group:', error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (name: string) => HcpGroup.updateHcpGroup(groupId!, { hcpGroupName: name }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['hcpGroups'] });
      queryClient.invalidateQueries({ queryKey: ['hcpGroupsAll'] });
      notifications.addUserActionNotification({
        type: UserAction.ResidentCreate,
        notificationType: 'success',
        message: t('hcpGroups.update.success', { name: data.hcpGroupName }),
      });
      onClose();
    },
    onError: (error) => {
      console.error('Error updating HCP group:', error);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupName.trim()) return;

    if (isEditMode) {
      updateMutation.mutate(groupName);
    } else {
      createMutation.mutate(groupName);
    }
  };

  return (
    <Modal onClose={onClose} style={{ maxWidth: 450 }}>
      <h3 className="text-center mb-30">{isEditMode ? t('hcpGroups.edit.title') : t('hcpGroups.create.title')}</h3>
      <FormPanel>
        <form onSubmit={handleSubmit}>
          <Input
            label={t('hcpGroups.form.groupName')}
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            required
            autoFocus
          />
          <div className="d-flex justify-content-end mt-30">
            <SubmitButton type="submit" loading={createMutation.isPending || updateMutation.isPending}>
              {t('global.submit')}
            </SubmitButton>
          </div>
        </form>
      </FormPanel>
    </Modal>
  );
};
